/**
 * Day Cell Detail Dialog Component
 *
 * Shows all events in a calendar cell in a larger, more readable format.
 * Allows users to view and interact with multiple overlapping events.
 */

import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarEvent } from 'angular-calendar';
import { AvailabilityEvent } from './availability';
import { ModuleVariantSlot } from '../../core/models';

@Component({
  selector: 'app-day-cell-detail-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <h2 mat-dialog-title>
          <span class="material-symbols-outlined">event</span>
          {{ formatDate(data.date) }}
        </h2>
        <button (click)="data.component.dialog.closeAll()" class="p-2 hover:bg-slate-100 rounded-full transition-colors flex items-center justify-center">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <div mat-dialog-content class="dialog-content">
        @if (data.events.length === 0) {
          <div class="no-events">
            <span class="material-symbols-outlined">event_busy</span>
            <p>Keine Events an diesem Tag</p>
          </div>
        } @else {
          <div class="events-list">
            @for (event of data.events; track event.id) {
              <div
                class="event-card"
                [class.available]="getAvailabilityEvent(event).availabilityStatus === 'available'"
                [class.unavailable]="
                  getAvailabilityEvent(event).availabilityStatus === 'unavailable'
                "
                [class.normal]="getAvailabilityEvent(event).availabilityStatus === 'normal'"
                [class.holiday]="getAvailabilityEvent(event).isHoliday"
                [class.clickable]="!getAvailabilityEvent(event).isHoliday"
                (click)="!getAvailabilityEvent(event).isHoliday && handleEventClick(event)"
              >
                <div class="event-header">
                  <div class="event-title">
                    <span class="material-symbols-outlined event-icon">
                      @if (getAvailabilityEvent(event).isHoliday) {
                        celebration
                      } @else if (getAvailabilityEvent(event).availabilityStatus === 'unavailable') {
                        cancel
                      } @else {
                        check_circle
                      }
                    </span>
                    <div class="variant-info">
                      @if (getAvailabilityEvent(event).meta?.locationShortname) {
                        <span class="location-name">{{
                          getAvailabilityEvent(event).meta.locationShortname
                        }}</span>
                      }
                      <span class="variant-name">{{
                        getAvailabilityEvent(event).variantName
                      }}</span>
                    </div>
                  </div>
                  <div class="event-status-badge">
                    @if (getAvailabilityEvent(event).isHoliday) {
                      <span class="status-label holiday">Feiertag</span>
                    } @else if (getAvailabilityEvent(event).availabilityStatus === 'unavailable') {
                      <span class="status-label unavailable">Nicht verfügbar</span>
                    } @else {
                      <span class="status-label available">Verfügbar</span>
                    }
                  </div>
                </div>

                <div class="event-details">
                  @if (getAvailabilityEvent(event).isHoliday) {
                    <div class="holiday-description">
                      {{ getAvailabilityEvent(event).holidayDescription }}
                    </div>
                  } @else {
                    @if (
                      getAvailabilityEvent(event).slots &&
                      getAvailabilityEvent(event).slots!.length > 0
                    ) {
                      <div class="time-slots">
                        @for (slot of getAvailabilityEvent(event).slots; track slot.start_time) {
                          <div class="time-slot">
                            <span class="material-symbols-outlined">access_time</span>
                            <span>{{ formatSlotTime(slot) }}</span>
                          </div>
                        }
                      </div>
                    }
                  }
                </div>

                @if (!getAvailabilityEvent(event).isHoliday) {
                  <div class="event-action">
                    <span class="hint">Klicken zum Status ändern</span>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>

      <div mat-dialog-actions class="dialog-actions">
        <button mat-button mat-dialog-close>Schließen</button>
      </div>
    </div>
  `,
  styles: `
    .dialog-container {
      display: flex;
      flex-direction: column;
      max-height: 80vh;
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem 1.5rem 0.5rem;
      border-bottom: 2px solid #e0e0e0;

      h2 {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0;
        font-size: 1.25rem;
        color: #1976d2;
      }
    }

    .dialog-content {
      padding: 1.5rem !important;
      overflow-y: auto;
      flex: 1;
    }

    .no-events {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      color: #999;

      .material-symbols-outlined {
        font-size: 64px;
        width: 64px;
        height: 64px;
        opacity: 0.3;
        margin-bottom: 1rem;
      }
    }

    .events-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .event-card {
      border: 2px solid #e0e0e0;
      border-radius: 8px;
      padding: 1rem;
      background: white;
      transition: all 0.2s ease;

      &.clickable {
        cursor: pointer;

        &:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }
      }

      &.available {
        border-color: #4caf50;
        background: #f1f8f4;
      }

      &.unavailable {
        border-color: #f44336;
        background: #fef5f5;
      }

      &.normal {
        border-color: #bdbdbd;
        background: #fafafa;
      }

      &.holiday {
        border-color: #f57f17;
        background: #fffbf0;
        cursor: not-allowed;
      }
    }

    .event-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.75rem;
    }

    .event-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .event-icon {
        font-size: 24px;
        width: 24px;
        height: 24px;
      }

      .variant-info {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      .location-name {
        font-size: 0.875rem;
        font-weight: 800;
        color: #1976d2;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .variant-name {
        font-weight: 600;
        font-size: 1rem;
        color: #333;
      }
    }

    .event-status-badge {
      .status-label {
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 600;

        &.available {
          background: #4caf50;
          color: white;
        }

        &.unavailable {
          background: #f44336;
          color: white;
        }

        &.normal {
          background: #bdbdbd;
          color: white;
        }

        &.holiday {
          background: #f57f17;
          color: white;
        }
      }
    }

    .event-details {
      margin-bottom: 0.5rem;
    }

    .holiday-description {
      font-style: italic;
      color: #666;
      padding: 0.5rem;
      background: #fff9e0;
      border-radius: 4px;
    }

    .time-slots {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .time-slot {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
      background: #f5f5f5;
      border-radius: 4px;
      font-weight: 500;

      .material-symbols-outlined {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: #1976d2;
      }
    }

    .event-action {
      text-align: center;
      margin-top: 0.5rem;

      .hint {
        font-size: 0.75rem;
        color: #999;
        font-style: italic;
      }
    }

    .dialog-actions {
      border-top: 1px solid #e0e0e0;
      padding: 1rem 1.5rem;
      display: flex;
      justify-content: flex-end;
    }
  `,
})
export class DayCellDetailDialogComponent {
  data = inject<{
    date: Date;
    events: CalendarEvent[];
    component: any;
  }>(MAT_DIALOG_DATA);

  /**
   * Format date for display
   */
  formatDate(date: Date): string {
    return format(date, 'EEEE, dd. MMMM yyyy', { locale: de });
  }

  /**
   * Cast CalendarEvent to AvailabilityEvent
   */
  getAvailabilityEvent(event: CalendarEvent): AvailabilityEvent {
    return event as AvailabilityEvent;
  }

  /**
   * Format slot time for display
   */
  formatSlotTime(slot: ModuleVariantSlot): string {
    const start = slot.start_time.substring(0, 5);
    const end = slot.end_time.substring(0, 5);
    return `${start} - ${end}`;
  }

  /**
   * Handle event click - delegate to parent component
   */
  handleEventClick(event: CalendarEvent): void {
    this.data.component.handleEventClick({ event });
  }
}
